import { useContext, useState } from "react";

// Project modules
import { GameContext } from "../../Context/GameContext";
import Banner from "../../Components/Banner/banner";
import Leaderboard from "../../Components/Leaderboard/leaderboard";
import RootLayout from "../../Components/Leaderboard/layout";
import "../../Components/Leaderboard/LeaderboardGlobal.css";
import "./LeaderboardPage.css";
import ActicaGroup from "../../Components/Branding/GroupLogos";

/**
 * LeaderboardPage
 * ----------------
 * Public leaderboard screen for a given event.
 * Behaviour:
 * - Reads `eventName` from context and infers branding for the banner.
 * - Hosts the auto-rotating leaderboard with a simple play/stop toggle.
 * - Presents a video (with poster fallback) and ACTICA group credit.
 * - *** :)
 */

// TYPES -------------------------------------------------------------
type Props = { admin: boolean };
type BrandingKey = "gemba" | "actica" | "ntegra";

// HELPERS -----------------------------------------------------------
const readBrandingForEvent = (eventId?: string | null): BrandingKey => {
  if (!eventId) return "gemba";
  try {
    const raw = localStorage.getItem("profile");
    const profile = raw ? JSON.parse(raw) : null;
    const branding = profile?.branding as unknown;
    return branding === "actica" || branding === "ntegra" ? branding : "gemba";
  } catch {
    return "gemba";
  }
};

// COMPONENT ---------------------------------------------------------
const LeaderboardPage: React.FC<Props> = ({ admin }) => {
  /** CONTEXT ------------------------------------------------------- */
  const { eventName } = useContext(GameContext);

  /** STATE --------------------------------------------------------- */
  const [auto, setAuto] = useState<boolean>(true);

  /** DERIVED ------------------------------------------------------- */
  const branding = readBrandingForEvent(eventName); // "gemba" | "actica" | "ntegra"
  const BANNER_HEIGHT_PX = 130;

  /** RENDER -------------------------------------------------------- */
  return (
    <RootLayout branding={branding}>
      <div
        className="leaderboard-page"
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1.5fr",
          columnGap: "80px",
          minHeight: 0,
        }}
      >
        {/* LEFT COLUMN */}
        <div
          style={{
            display: "grid",
            gridTemplateRows: `${BANNER_HEIGHT_PX}px auto 1fr auto`,
            rowGap: "12px",
            minHeight: 0,
          }}
        >
          {/* Banner */}
          <div className="banner-slot" style={{ width: "100%", height: "100%" }}>
            <Banner branding={branding} />
          </div>

          {/* Event name */}
          <div className="event-title">{eventName}</div>

          {/* Video (falls back to poster if load error) */}
          <div
            className="leaderboard-page__video-frame"
            style={{
              width: "100%",
              height: "100%",
              minHeight: 0,
              backgroundImage: "url('/assets/images/Robots-Scoreboard-Fallback.png')",
              backgroundSize: "contain",
              backgroundRepeat: "no-repeat",
              backgroundPosition: "center",
              borderRadius: "12px",
              overflow: "hidden",
            }}
          >
            <video
              className="leaderboard-page__video"
              poster="/assets/images/Robots-Scoreboard-Fallback.png"
              autoPlay
              muted
              loop
              playsInline
              preload="metadata"
              aria-hidden="true"
              onError={(e) => {
                (e.currentTarget as HTMLVideoElement).style.display = "none";
              }}
            >
              <source src="/assets/video/Robots-Scoreboard.webm" type="video/webm" />
              <img
                src="/assets/images/Robots-Scoreboard-Fallback.png"
                alt=""
                aria-hidden="true"
                className="leaderboard-page__video-fallback"
              />
            </video>
          </div>

          {/* Credits */}
          <div className="actica-wrapper">
            <ActicaGroup />
          </div>
        </div>

        {/* RIGHT COLUMN */}
        <div
          style={{
            display: "grid",
            gridTemplateRows: "auto 1fr",
            rowGap: "12px",
            minHeight: 0,
          }}
        >
          {/* Auto-rotate toggle */}
          <div className="control-buttons">
            <button
              className={auto ? "stop" : "play"}
              onClick={() => setAuto(!auto)}
              aria-label={auto ? "Stop auto-rotate" : "Start auto-rotate"}
            />
          </div>

          {/* Leaderboard */}
          <div style={{ minHeight: 0, overflow: "hidden" }}>
            <div style={{ height: "100%", overflow: "auto" }}>
              <Leaderboard auto={auto} admin={admin} />
            </div>
          </div>
        </div>
      </div>
    </RootLayout>
  );
};

export default LeaderboardPage;